import {
  convertBooleanToNumber,
  convertTimestampToDate,
  getExercisedData,
  getHourFromTimestamp,
  getTime,
  convertForceSellEvent,
  convertSmartOrderConfigType,
  convertSecuritiesKind,
} from 'utils/securities/formatter';

import type {
  PortfolioState,
  PortfolioStateItem,
} from 'global/Securities/types/store/portfolio';
import {
  OrderStateItem,
  OrderAction,
  iOrderSide,
  PlatformOrderTypes,
  ActionTarget as ActionTargetStr,
  OrderSide,
  OrderLeverageType,
} from 'global/Securities/types/store/order';
import {
  ActionTarget,
  Order_GTC_GTCStatus,
  Side,
  SmartOrderLabelType,
} from '@stockbitgroup/protos/securities/trm/core/portfolio/entity/v2/portfolio_pb';
import {
  OrderList,
  OrderDetail,
} from '../../../@types/securities/websocket/orderWS';

export const convertSideToAction = (side: iOrderSide): OrderAction =>
  side === iOrderSide.BUY ? OrderAction.Buy : OrderAction.Sell;

const portfolioSummary = (portfolio: PortfolioSummary): PortfolioState => {
  const { trading, amount, profitLoss, gain, equity, debt } = portfolio ?? {};
  return {
    tradingbalance: trading?.balance,
    amount_invested: amount?.invested,
    amount_allocated: amount?.allocated,
    amount_creditlimit: amount?.creditLimit,
    unrealised_profitloss: profitLoss?.unrealised,
    realised_profitloss: profitLoss?.realised,
    profitloss: profitLoss?.net,
    gain,
    equity,
    debt_ratio: debt?.ratio,
    debt_total: debt?.total,
    debt_market_value: debt?.marketValue,
  };
};

const portfolioItem = (data: PortfolioDetail): PortfolioStateItem => {
  const { asset, company, exercise, flags, price, qty, symbol, isSharia } =
    data ?? {};

  return {
    allow_order: convertBooleanToNumber(flags?.allowOrder),
    amount_invested: asset?.amountInvested,
    available_lot: qty?.available.lot,
    available_share: qty?.available.share,
    balance_lot: qty?.balance.lot,
    balance_share: qty?.balance.share,
    company_name: company?.name,
    exercise_data: getExercisedData(exercise),
    exercise_type: exercise?.type,
    have_smartorder: flags?.haveSmartOrder,
    price_average: price?.average.price,
    price_average_fee: price?.average.fee,
    price_latest: price?.latest,
    stock_on_hand: qty?.stockOnHand,
    symbol,
    total: asset?.amountInvested,
    unrealised_gain: asset?.unrealised.gain,
    unrealised_marketvalue: asset?.unrealised.marketValue,
    unrealised_profitloss: asset?.unrealised.profitLoss,
    can_buy: flags?.canBuy,
    can_sell: flags?.canSell,
    is_sharia: isSharia,
  };
};

const portfolioList = (portfolio: PortfolioList): PortfolioState => {
  const { summary, smartOrderInfo, results, dayTrade } = portfolio ?? {};

  return {
    tradingbalance: summary?.trading?.balance,
    amount_invested: summary?.amount?.invested,
    amount_allocated: summary?.amount?.allocated,
    amount_creditlimit: summary?.amount?.creditLimit,
    unrealised_profitloss: summary?.profitLoss?.unrealised,
    realised_profitloss: summary?.profitLoss?.realised,
    profitloss: summary?.profitLoss?.net,
    gain: summary?.gain,
    equity: summary?.equity,
    show_create_smartorder: smartOrderInfo?.canCreate,
    auto_order_info: smartOrderInfo?.info,
    result: results?.map((result) => portfolioItem(result)),
    day_trade: dayTrade?.map((result) => portfolioItem(result)),
    debt_ratio: summary?.debt?.ratio,
    debt_total: summary?.debt?.total,
    debt_market_value: summary?.debt?.marketValue,
  };
};

const orderDetail = (order: OrderDetail): OrderStateItem => {
  const {
    action,
    amount,
    dayTrade,
    gtc,
    marketOrderId,
    message,
    orderId,
    price,
    qty,
    side,
    smartOrder,
    statusText,
    symbol,
    time,
    isSharia,
    securitiesKind,
  } = order ?? {};

  const securities_kind = convertSecuritiesKind(securitiesKind);
  const isBond = securities_kind === 'SECURITIES_KIND_BOND';

  const platforms = {
    0: PlatformOrderTypes.UNSPECIFIED,
    1: PlatformOrderTypes.LIMIT,
    2: PlatformOrderTypes.FAK,
  };

  const actionTargets = {
    0: ActionTargetStr.UNSPECIFIED,
    1: ActionTargetStr.REGULAR,
    2: ActionTargetStr.DAY_TRADE,
    3: ActionTargetStr.LEGACY_SMARTORDER,
    4: ActionTargetStr.TRAILING_STOP,
    5: ActionTargetStr.STOP_ORDER,
    6: ActionTargetStr.BRACKET_ORDER,
  };

  const MapGtcInt: Record<Order_GTC_GTCStatus, OrderGTCStatus> = {
    [Order_GTC_GTCStatus.GTC_STATUS_UNSPECIFIED]: 'GTC_STATUS_UNSPECIFIED',
    [Order_GTC_GTCStatus.GTC_STATUS_OPEN]: 'GTC_STATUS_OPEN',
    [Order_GTC_GTCStatus.GTC_STATUS_MATCH]: 'GTC_STATUS_MATCH',
    [Order_GTC_GTCStatus.GTC_STATUS_WITHDRAWN]: 'GTC_STATUS_WITHDRAWN',
  };

  const MapOrderSide: Record<Side, OrderSide> = {
    [Side.UNSPECIFIED]: OrderSide.Unspecified,
    [Side.BUY]: OrderSide.Buy,
    [Side.SELL]: OrderSide.Sell,
  };

  const MapSmartOrderLabel: Record<SmartOrderLabelType, SmartOrderLabelStr> = {
    [SmartOrderLabelType.UNSPECIFIED]: 'SMART_ORDER_LABEL_TYPE_UNSPECIFIED',
    [SmartOrderLabelType.AUTO_BUY]: 'SMART_ORDER_LABEL_TYPE_AUTO_BUY',
    [SmartOrderLabelType.STOP_LOSS]: 'SMART_ORDER_LABEL_TYPE_STOP_LOSS',
    [SmartOrderLabelType.TAKE_PROFIT]: 'SMART_ORDER_LABEL_TYPE_TAKE_PROFIT',
    [SmartOrderLabelType.TRAILING_STOP]: 'SMART_ORDER_LABEL_TYPE_TRAILING_STOP',
    [SmartOrderLabelType.LIMIT_IF_TOUCHED]:
      'SMART_ORDER_LABEL_TYPE_LIMIT_IF_TOUCHED',
    [SmartOrderLabelType.BRACKET_ORDER]: 'SMART_ORDER_LABEL_TYPE_BRACKET_ORDER',
    [SmartOrderLabelType.VOLUME_TRIGGER_ORDER]:
      'SMART_ORDER_LABEL_TYPE_VOLUME_TRIGGER_ORDER',
  };

  const leverageType = {
    0: OrderLeverageType.Unspecified,
    1: OrderLeverageType.TradingBalance,
    2: OrderLeverageType.TradingLimit,
    3: OrderLeverageType.BasicLimit,
    4: OrderLeverageType.TempLimit,
    5: OrderLeverageType.DayTrade,
  };

  return {
    action: convertSideToAction(side),
    amend_target: actionTargets[action.amendTarget],
    amend_action:
      action.amendTarget !== ActionTarget.UNSPECIFIED || action.canAmend,
    amount: amount?.amount,
    amount_fee: amount?.fee.amount,
    amount_invested: amount?.invested,
    amount_invested_fee: amount?.fee.invested,
    amount_matched: isBond ? amount?.fee?.invested : amount?.matched,
    amount_matched_broker_fee: amount?.fee.matched.broker,
    amount_matched_exchange_fee: amount?.fee.matched.exchange,
    amount_matched_fee: amount?.fee.matched.amount,
    amount_matched_total: amount?.matchedTotal,
    amount_open: amount?.open,
    amount_open_fee: amount?.fee.open,
    cancel_target: actionTargets[action.cancelTarget],
    cancel_action:
      action.cancelTarget !== ActionTarget.UNSPECIFIED || action.canCancel,
    config_type: convertSmartOrderConfigType(
      smartOrder?.configType?.toString(),
    ),
    gtc: convertBooleanToNumber(gtc?.isGtc),
    label: smartOrder?.label,
    market_orderid: marketOrderId,
    message,
    order_done: qty?.lotDone,
    order_open: qty?.lotOpen,
    order_total: qty?.lotTotal,
    orderid: orderId,
    price: price?.order,
    price_average: price?.average.price,
    price_average_fee: price?.average.fee,
    price_order: price?.order,
    smart_orderid: smartOrder?.orderId,
    smart_order_bracket_order_child:
      smartOrder?.bracketOrderChildren
        .sort((a, b) => {
          if (a.label === SmartOrderLabelType.STOP_LOSS) return -1;
          if (b.label === SmartOrderLabelType.STOP_LOSS) return 1;
          return 0;
        })
        .map((bo) => {
          const child: BracketOrderChild = {
            amount: bo.amount,
            gtc: {
              is_gtc: bo.gtc?.isGtc ?? false,
              status: MapGtcInt[bo.gtc?.status || 0],
            },
            label: MapSmartOrderLabel[bo.label],
            symbol: bo.symbol,
            order_id: bo.orderId,
            price: {
              order: bo.price?.order ?? 0,
              price_different_percentage:
                bo.price?.priceDifferencePercentage ?? 0,
            },
            side: MapOrderSide[bo.side],
            status_text: bo.statusText,
            profit_loss: bo.profitLoss,
            action: {
              amend_target:
                actionTargets[
                  bo.action?.amendTarget ?? ActionTarget.UNSPECIFIED
                ],
              cancel_target:
                actionTargets[
                  bo.action?.cancelTarget ?? ActionTarget.UNSPECIFIED
                ],
            },
            lot_done: bo.lotDone,
            lot_ordered: bo.lotOrdered,
            amount_done: bo.amountDone,
            amount_ordered: bo.amountOrdered,
          };
          return child;
        }) || [],
    smart_order_parent_id: smartOrder.parentId,
    smart_order_parent_price: smartOrder.parentPrice,
    status: statusText,
    symbol,
    securities_kind,
    time: getHourFromTimestamp(getTime(time?.open)),
    time_match: convertTimestampToDate(getTime(time?.match)),
    time_open: convertTimestampToDate(getTime(time?.open)),
    trigger_price: smartOrder?.triggerPrice,
    day_trade_is_day_trade: dayTrade?.isDayTrade,
    day_trade_force_sell_event: convertForceSellEvent(dayTrade?.forceSellEvent),
    day_trade_message: dayTrade?.message,
    is_sharia: isSharia,
    trailing_stop: smartOrder?.trailingStop
      ? {
          id: smartOrder?.trailingStop?.id.toString() || '',
          price_algorithm: {
            type: smartOrder?.trailingStop?.priceAlgorithm?.type,
            value: smartOrder?.trailingStop?.priceAlgorithm?.value,
          },
          stop_price: {
            currency_code: smartOrder?.trailingStop?.stopPrice?.currencyCode,
            units: smartOrder?.trailingStop?.stopPrice?.units.toString(),
            nanos: smartOrder?.trailingStop?.stopPrice?.nanos,
          },
        }
      : null,
    platform_order_type: platforms[order.platformOrderType],
    leverage_type: leverageType[order.leverageType ?? 0],
  };
};

const orderList = (response: OrderList): OrderStateItem[] => {
  const { orders } = response ?? {};

  return (
    orders
      // Skip child order that still has parentId
      ?.filter((order) => !order.smartOrder.parentId)
      .map((order) => orderDetail(order))
  );
};

export default {
  portfolioSummary,
  portfolioItem,
  portfolioList,
  orderDetail,
  orderList,
};
